import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MetaTags from 'react-meta-tags';
import { useNavigate, useLocation, Link } from "react-router-dom";
import '../styles/loginpage.css';           // NEW stylesheet (create below)
import Logo from '../images/new_logo.png';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { Form, Input, Button, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { GoogleLogin } from "@react-oauth/google";
import { FaGoogle } from 'react-icons/fa';
import { AUTH_PROVIDER, GOOGLE_CLIENT_ID, createGooglePassword, getGoogleProfile } from '../utils/googleAuth';

const LOGIN_URL = 'https://epolylearning.com/Services/polyquiz.svc/GetUserData';

const LoginPage = () => {
  const [loginerror, setLoginError] = useState(false);
  const [activationMsg, setActivationMsg] = useState(false);
  const [inactiveMsg, setInactiveMsg] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState(false);
  const [googleEmailAccountMsg, setGoogleEmailAccountMsg] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // NEW: processing state
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { Title } = Typography;

  const itemStr = localStorage.getItem('isLogin');
  const item = itemStr ? JSON.parse(itemStr) : null;
  const now = new Date();

  const [form] = Form.useForm();

  const checkActivation = () => {
    if (location && location.state === 'activationSuccess') {
      setActivationMsg(true);
    }
  };

  function setUser(key, email, username, firstname, ttl, authProvider) {
    const nowLocal = new Date();
    const itemLocal = {
      userid: key,
      email: email,
      username: username,
      firstname: firstname,
      authProvider: authProvider,
      expiry: nowLocal.getTime() + ttl,
    };
    localStorage.setItem('isLogin', JSON.stringify(itemLocal));

    // navigate as before
    const redirectTo = location?.state?.redirectAfterLogin || '/';
    navigate(redirectTo);

    // ensure Navbar (which is fixed outside animated route) picks up new login state:
    // reload the page shortly after navigation so the global header reads localStorage again.
    // small timeout lets react-router complete navigation before reload.
    setTimeout(() => {
      try {
        window.location.reload();
      } catch (e) {
        // fallback: nothing
      }
    }, 120);
  }

  const loginUser = async (payload) => {
    const json = JSON.stringify(payload);
    const headers = { 'Content-Type': 'application/json' };

    const response = await axios.post(LOGIN_URL, json, { headers });
    return response.data?.GetUserDataResult?.LoginList?.[0];
  };

  async function onFinish(data) {
    setLoginError(false);
    setInactiveMsg(false);
    setActivationMsg(false);
    setGoogleLoginError(false);
    setGoogleEmailAccountMsg(false);
    setIsProcessing(true); // NEW: show spinner and disable button

    try {
      const first = await loginUser({
        email: data.email,
        pass: data.password,
        authProvider: AUTH_PROVIDER.EMAIL,
        loginType: AUTH_PROVIDER.EMAIL,
        isGoogleSignup: false,
      });

      if (first !== undefined && first.IsActivated === false) {
        setInactiveMsg(true);
      } else if (first !== undefined && first.IsActivated === true) {
        // keep the very long ttl you had (2592000000000)
        setUser(first.UserId, first.Email, first.UserName, first.FirstName, 2592000000000, AUTH_PROVIDER.EMAIL);
      } else {
        setLoginError(true);
      }
    } catch (error) {
      console.error(error);
      setLoginError(true);
    } finally {
      setIsProcessing(false); // NEW: stop spinner
    }
  }

  async function handleGoogleLogin(credentialResponse) {
    setLoginError(false);
    setInactiveMsg(false);
    setActivationMsg(false);
    setGoogleLoginError(false);
    setGoogleEmailAccountMsg(false);
    setIsGoogleProcessing(true);

    try {
      const profile = getGoogleProfile(credentialResponse);

      if (!profile?.email) {
        setGoogleLoginError(true);
        return;
      }

      const first = await loginUser({
        email: profile.email,
        pass: createGooglePassword(profile.email, profile.googleId),
        authProvider: AUTH_PROVIDER.GOOGLE,
        loginType: AUTH_PROVIDER.GOOGLE,
        signupType: AUTH_PROVIDER.GOOGLE,
        isGoogleSignup: true,
        googleId: profile.googleId,
        googleCredential: profile.credential,
      });

      if (first !== undefined && first.IsActivated === true) {
        setUser(first.UserId, first.Email, first.UserName, first.FirstName, 2592000000000, AUTH_PROVIDER.GOOGLE);
      } else {
        setGoogleEmailAccountMsg(true);
      }
    } catch (error) {
      console.error(error);
      setGoogleLoginError(true);
    } finally {
      setIsGoogleProcessing(false);
    }
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    try {
      if (itemStr && item && item.expiry > now.getTime() && location.state !== 'activationSuccess') {
        const redirectTo = location?.state?.redirectAfterLogin || '/';
        navigate(redirectTo);
      }
    } catch (e) {
      // ignore parse errors
    }
    checkActivation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MetaTags>
        <title>Login to your Account - PolyQuiz</title>
        <meta id="meta-description" name="description" />
      </MetaTags>

      <div className="login-page-root">
        <div className="login-card">
          <div className="login-header">
            <img className="login-logo" src={Logo} alt="Logo" />
            <p className="auth-subtext">
              Sign in to continue your exam prep with online mock tests, timed practice, detailed solutions,
              and progress tracking across Medical, IT, Professional, Legal and more exams.
            </p>
            <div className="login-title-wrap">
              <h1 className="login-title">Welcome Back</h1>
              <div className="login-subtitle">Sign in to your PolyQuizz account</div>
            </div>
          </div>

          {/* Activation / invalid messages (keeps your existing text) */}
          {activationMsg && (
            <div className="activation-msg info">
              <p>Congratulations !!! Your account has been successfully verified. You can Login now.</p>
            </div>
          )}

          {location.state === 'invalidRequest' && (
            <div className="activation-msg info">
              <p>Either this account is already verified or Invalid request.</p>
            </div>
          )}

          <div className="login-form-wrap">
            <Form
              form={form}
              name="normal_login"
              className="login-form"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              layout="vertical"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'The input is not valid E-mail!' },
                  { required: true, message: 'Please input your E-mail!' },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="your.email@example.com"
                  className="styled-input"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please input your Password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="••••••••"
                  className="styled-input"
                  iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              {loginerror && <div className="login-message error">Invalid Email or Password. Try Again.</div>}

              {inactiveMsg && (
                <div className="login-message error">
                  This account is not verified yet. Please check email used at the time of registration for activation mail.
                  If you are unable to find the activation mail, <Link to="/auth/resend-activation-mail">click here</Link> to resend the email.
                </div>
              )}

              <Form.Item className="login-submit">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-btn"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="btn-spinner login-spinner" aria-hidden="true"></span>
                      Signing in…
                    </>
                  ) : (
                    <>
                      <span className="login-btn-icon" aria-hidden>↪</span>
                      Sign In
                    </>
                  )}
                </Button>
              </Form.Item>
            </Form>

            <div className="auth-divider" role="separator" aria-label="or">
              <span>or</span>
            </div>

            <div className="google-auth-section">
              <div className="google-auth-label">
                <FaGoogle aria-hidden="true" />
                <span>Continue with Google</span>
              </div>
              {GOOGLE_CLIENT_ID ? (
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => setGoogleLoginError(true)}
                  text="signin_with"
                  shape="rectangular"
                  size="large"
                  width="100%"
                />
              ) : (
                <div className="login-message info">
                  Google login is not configured for this environment.
                </div>
              )}
              {isGoogleProcessing && (
                <div className="login-message info">
                  Signing in with Google...
                </div>
              )}
              {googleEmailAccountMsg && (
                <div className="login-message error">
                  This email is registered with email/password. Please log in with your email and password.
                </div>
              )}
              {googleLoginError && (
                <div className="login-message error">
                  Google login could not be completed. Please try again.
                </div>
              )}
            </div>

            <div className="login-footer-links">
              <Link to="/auth/reset-forgot-password" className="forgot-link">Forgot Password?</Link>
              <div className="signup-line">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  state={{ redirectAfterLogin: location?.state?.redirectAfterLogin || '/' }}
                  className="signup-link"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default LoginPage;
